import { EVENT_BATCH_MAX } from '@apm/contracts';
/**
 * 이벤트 큐. **전송 실패가 재생을 방해해서는 안 된다** (`docs/03-api-spec.md`).
 * 그래서 이 클래스의 어떤 메서드도 throw 하지 않는다.
 *
 * 큐 상한은 `@apm/contracts` 의 `EVENT_BATCH_MAX`(50). 서버 배치 엔드포인트의 상한과
 * 같은 상수를 쓴다 — 두 곳에 숫자를 두면 큐가 서버가 거부하는 크기를 보내게 된다.
 * 가득 차면 **오래된 것부터 버린다.** 최신 이벤트가 더 쓸모 있고, 무한히 쌓으면
 * 오프라인 상태의 리시버가 메모리를 먹는다.
 *
 * **타이머를 쓰지 않는다.** `setInterval` 은 코어가 참조할 수 없는 플랫폼 API 다
 * (`lib: ES2022` 에 없다). 재시도는 다음 `enqueue`/`flush` 가 태운다 — 전환마다
 * 이벤트가 발생하므로 실기에서 재시도 기회는 항목당 최소 한 번씩 온다.
 * 타이머가 필요해지면 `TimerAdapter` 를 추가할 일이지 코어에 넣을 일이 아니다.
 */
export class EventQueue {
    network;
    serverUrl;
    queue = [];
    sending = false;
    dropped = 0;
    constructor(network, serverUrl) {
        this.network = network;
        this.serverUrl = serverUrl;
    }
    /** 큐에 넣고 전송을 시도한다. 결과를 기다리지 않는다. */
    enqueue(event) {
        if (this.queue.length >= EVENT_BATCH_MAX) {
            this.queue.shift();
            this.dropped += 1;
        }
        this.queue.push(event);
        void this.flush();
    }
    /**
     * 큐를 비운다. 실패하면 보낸 것을 큐 앞으로 되돌려 다음 기회에 다시 시도한다.
     * **절대 reject 하지 않는다.**
     *
     * **비울 때까지 돈다.** 전송 중에 들어온 이벤트를 남겨두면 `sending` 가드에 걸려
     * 아무도 그것을 태우지 않는다 — 실패가 없어도 가장 최근 이벤트가 계속 한 박자 밀린다.
     * 실패했을 때만 루프를 빠져나가 다음 기회로 넘긴다 (그래야 오프라인에서 회전하지 않는다).
     */
    async flush() {
        if (this.sending || this.queue.length === 0)
            return;
        this.sending = true;
        const base = this.serverUrl.replace(/\/+$/, '');
        try {
            while (this.queue.length > 0) {
                const batch = this.queue.splice(0, EVENT_BATCH_MAX);
                try {
                    if (batch.length === 1) {
                        await this.network.post(`${base}/v1/events`, batch[0]);
                    }
                    else {
                        await this.network.post(`${base}/v1/events/batch`, batch);
                    }
                }
                catch {
                    this.requeue(batch);
                    break;
                }
            }
        }
        finally {
            this.sending = false;
        }
    }
    /** 실패한 배치를 앞으로 되돌린다. 되돌린 뒤 넘치면 오래된 것부터 버린다. */
    requeue(batch) {
        this.queue = [...batch, ...this.queue];
        if (this.queue.length > EVENT_BATCH_MAX) {
            this.dropped += this.queue.length - EVENT_BATCH_MAX;
            this.queue = this.queue.slice(this.queue.length - EVENT_BATCH_MAX);
        }
    }
    /** 진단용. 디버그 패널이 "이벤트가 새고 있는가"를 보는 유일한 창이다. */
    get stats() {
        return { pending: this.queue.length, dropped: this.dropped };
    }
}
//# sourceMappingURL=event-queue.js.map